// Date-of-birth helpers. DOBs are stored as `mm/dd/yyyy` strings everywhere
// in the codebase (see `Attendee.birthdate` in `types.ts` and the input
// formatter in `attendee-form.tsx`), so all helpers here accept that shape.

// mm/dd/yyyy with month 01-12, day 01-31, year 1900-2099. Validates shape
// only — does not catch impossible dates like Feb 30 (matches the original
// regex from `attendee-form.tsx`, which it replaced).
//
// The year group wraps the whole 4-digit year — `((?:19|20)\d{2})` — not
// just the century. An earlier version captured only `(19|20)`, which made
// `Number(y) === 20` and produced a 2006-year-old "age" that failed the
// sanity check, silently hiding the age on the Roster.
export const DOB_REGEX =
  /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/((?:19|20)\d{2})$/;

// Returns age in completed years today, or null if the string is missing,
// malformed, or implausible (future date / > 130 years old). We parse
// explicitly rather than using Date(string) because `mm/dd/yyyy` parses
// inconsistently across browsers/locales.
export const ageFromDob = (dob?: string | null): number | null => {
  if (!dob) return null;
  const match = DOB_REGEX.exec(dob.trim());
  if (!match) return null;
  const [, m, d, y] = match;
  const month = Number(m);
  const day = Number(d);
  const year = Number(y);

  const today = new Date();
  let age = today.getFullYear() - year;
  // Subtract a year if today is before this year's birthday (compare on
  // month/day directly to avoid Date constructor quirks on Feb 29 etc.).
  const todayMonth = today.getMonth() + 1;
  const todayDay = today.getDate();
  if (
    todayMonth < month ||
    (todayMonth === month && todayDay < day)
  ) {
    age -= 1;
  }
  if (age < 0 || age > 130) return null;
  return age;
};

// Display helper: returns `mm/dd/yyyy (N y.o.)` when the DOB is valid, the
// raw DOB string when it's present but malformed (so we don't lose data),
// or an empty string when there's no DOB at all. Used wherever DOB is shown
// to volunteers / admins.
export const formatDobWithAge = (dob?: string | null): string => {
  if (!dob) return '';
  const age = ageFromDob(dob);
  if (age === null) return dob;
  return `${dob} (${age} y.o.)`;
};
